import { Controller } from "@hotwired/stimulus"

// Crop rectangle locked to 2:1 aspect ratio (matches DPK thumbnail display ratio)
const ASPECT = 2 / 1

export default class extends Controller {
  static targets = ["modal", "image", "overlay", "cropBox", "preview", "coords", "saveBtn"]

  connect() {
    this.photoId = null
    this.updateUrl = null
    this.cropX = 0
    this.cropY = 0
    this.cropW = 100
    this.cropH = 100
    this.imgW = 1 // natural image width
    this.imgH = 1 // natural image height
    this.dragging = null
    this.startMouse = null
    this.startCrop = null

    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)
  }

  open(event) {
    const btn = event.currentTarget
    this.photoId = btn.dataset.photoId
    this.updateUrl = btn.dataset.updateUrl

    const savedX = parseFloat(btn.dataset.cropX)
    const savedY = parseFloat(btn.dataset.cropY)
    const savedW = parseFloat(btn.dataset.cropW)
    const savedH = parseFloat(btn.dataset.cropH)

    this.imageTarget.src = btn.dataset.photoUrl
    this.previewTarget.src = btn.dataset.photoUrl
    this.imageTarget.onload = () => {
      this.imgW = this.imageTarget.naturalWidth || 1
      this.imgH = this.imageTarget.naturalHeight || 1

      // Use saved crop if it's custom, otherwise compute default max 4:3 crop
      if (savedW > 0 && savedW < 100.01 && (savedX !== 0 || savedY !== 0 || savedW !== 100 || savedH !== 100)) {
        this.cropX = savedX
        this.cropY = savedY
        this.cropW = savedW
        this.cropH = savedH
      } else {
        this._setDefaultCrop()
      }

      this._updateCropBox()
      this._updatePreview()
      this._updateCoords()
    }

    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this.dragging = null
  }

  // Compute the largest 4:3 crop that fits the image, centered
  _setDefaultCrop() {
    // In pixel terms: cropW_px / cropH_px = ASPECT
    // cropW% = cropW_px / imgW * 100, cropH% = cropH_px / imgH * 100
    // So: (cropW/100 * imgW) / (cropH/100 * imgH) = ASPECT
    // => cropH = cropW * imgW / (imgH * ASPECT)

    let w = 100
    let h = w * this.imgW / (this.imgH * ASPECT)

    if (h > 100) {
      h = 100
      w = h * this.imgH * ASPECT / this.imgW
    }

    this.cropW = w
    this.cropH = h
    this.cropX = (100 - w) / 2
    this.cropY = (100 - h) / 2
  }

  // Derive crop height from width to maintain 4:3 pixel aspect ratio
  _heightForWidth(w) {
    return w * this.imgW / (this.imgH * ASPECT)
  }

  // Derive crop width from height to maintain 4:3 pixel aspect ratio
  _widthForHeight(h) {
    return h * this.imgH * ASPECT / this.imgW
  }

  startDrag(event) {
    event.preventDefault()
    const handle = event.target.dataset.handle
    if (handle) {
      this.dragging = handle
    } else if (event.target.closest("[data-crop-region]")) {
      this.dragging = "move"
    } else {
      // Click outside crop: start drawing new crop from this point
      this.dragging = "draw"
      const rect = this.overlayTarget.getBoundingClientRect()
      this.cropX = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100))
      this.cropY = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
      this.cropW = 0
      this.cropH = 0
    }

    this.startMouse = { x: event.clientX, y: event.clientY }
    this.startCrop = { x: this.cropX, y: this.cropY, w: this.cropW, h: this.cropH }

    document.addEventListener("mousemove", this._onMouseMove)
    document.addEventListener("mouseup", this._onMouseUp)
  }

  _onMouseMove(event) {
    if (!this.dragging) return
    event.preventDefault()

    const rect = this.overlayTarget.getBoundingClientRect()
    const dx = ((event.clientX - this.startMouse.x) / rect.width) * 100
    const dy = ((event.clientY - this.startMouse.y) / rect.height) * 100
    const s = this.startCrop

    if (this.dragging === "move") {
      this.cropX = Math.max(0, Math.min(100 - s.w, s.x + dx))
      this.cropY = Math.max(0, Math.min(100 - s.h, s.y + dy))
    } else if (this.dragging === "draw") {
      let w = Math.max(5, dx)
      let h = this._heightForWidth(w)
      // Clamp to image bounds
      if (s.x + w > 100) { w = 100 - s.x; h = this._heightForWidth(w) }
      if (s.y + h > 100) { h = 100 - s.y; w = this._widthForHeight(h) }
      this.cropW = Math.max(5, w)
      this.cropH = Math.max(this._heightForWidth(5), h)
    } else {
      this._resizeFromHandle(dx, dy)
    }

    this._updateCropBox()
    this._updatePreview()
    this._updateCoords()
  }

  _resizeFromHandle(dx, dy) {
    const s = this.startCrop
    const MIN_W = 5
    const MIN_H = this._heightForWidth(MIN_W)
    let x = s.x, y = s.y, w = s.w, h = s.h

    // For corner handles, use the dominant axis (whichever moved more) and derive the other
    // For edge handles, use the dragged axis and derive the other
    const isHoriz = this.dragging === "e" || this.dragging === "w"
    const isVert = this.dragging === "n" || this.dragging === "s"
    const isCorner = !isHoriz && !isVert

    if (isCorner) {
      // Use horizontal delta as the driver
      if (this.dragging.includes("e")) {
        w = Math.max(MIN_W, Math.min(100 - s.x, s.w + dx))
        h = this._heightForWidth(w)
      } else if (this.dragging.includes("w")) {
        const newX = Math.max(0, Math.min(s.x + s.w - MIN_W, s.x + dx))
        w = s.w - (newX - s.x)
        h = this._heightForWidth(w)
        x = newX
      }

      // Adjust vertical origin for north handles
      if (this.dragging.includes("n")) {
        y = s.y + s.h - h
      }

      // Clamp to bounds
      if (y < 0) { y = 0; h = s.y + s.h; w = this._widthForHeight(h); if (this.dragging.includes("w")) x = s.x + s.w - w }
      if (y + h > 100) { h = 100 - y; w = this._widthForHeight(h); if (this.dragging.includes("w")) x = s.x + s.w - w }
    } else if (isHoriz) {
      if (this.dragging === "e") {
        w = Math.max(MIN_W, Math.min(100 - s.x, s.w + dx))
      } else {
        const newX = Math.max(0, Math.min(s.x + s.w - MIN_W, s.x + dx))
        w = s.w - (newX - s.x)
        x = newX
      }
      h = this._heightForWidth(w)
      // Keep centered vertically relative to original midpoint
      const midY = s.y + s.h / 2
      y = midY - h / 2
      if (y < 0) { y = 0 }
      if (y + h > 100) { y = 100 - h }
    } else {
      if (this.dragging === "s") {
        h = Math.max(MIN_H, Math.min(100 - s.y, s.h + dy))
      } else {
        const newY = Math.max(0, Math.min(s.y + s.h - MIN_H, s.y + dy))
        h = s.h - (newY - s.y)
        y = newY
      }
      w = this._widthForHeight(h)
      // Keep centered horizontally relative to original midpoint
      const midX = s.x + s.w / 2
      x = midX - w / 2
      if (x < 0) { x = 0 }
      if (x + w > 100) { x = 100 - w }
    }

    this.cropX = x
    this.cropY = y
    this.cropW = Math.max(MIN_W, w)
    this.cropH = Math.max(MIN_H, h)
  }

  _onMouseUp() {
    this.dragging = null
    document.removeEventListener("mousemove", this._onMouseMove)
    document.removeEventListener("mouseup", this._onMouseUp)
  }

  resetCrop() {
    this._setDefaultCrop()
    this._updateCropBox()
    this._updatePreview()
    this._updateCoords()
  }

  _updateCropBox() {
    const box = this.cropBoxTarget
    box.style.left = `${this.cropX}%`
    box.style.top = `${this.cropY}%`
    box.style.width = `${this.cropW}%`
    box.style.height = `${this.cropH}%`

    const shades = this.overlayTarget.querySelectorAll("[data-shade]")
    if (shades.length === 4) {
      shades[0].style.cssText = `position:absolute;top:0;left:0;width:100%;height:${this.cropY}%;background:rgba(0,0,0,0.5);`
      shades[1].style.cssText = `position:absolute;bottom:0;left:0;width:100%;height:${100 - this.cropY - this.cropH}%;background:rgba(0,0,0,0.5);`
      shades[2].style.cssText = `position:absolute;top:${this.cropY}%;left:0;width:${this.cropX}%;height:${this.cropH}%;background:rgba(0,0,0,0.5);`
      shades[3].style.cssText = `position:absolute;top:${this.cropY}%;right:0;width:${100 - this.cropX - this.cropW}%;height:${this.cropH}%;background:rgba(0,0,0,0.5);`
    }
  }

  _updatePreview() {
    const p = this.previewTarget
    const container = p.parentElement
    const cRect = container.getBoundingClientRect()
    const cW = cRect.width
    const cH = cRect.height

    if (cW === 0 || cH === 0 || this.cropW === 0 || this.cropH === 0) return

    // Scale image so the crop region exactly fills the preview container
    const imgW = cW * 100 / this.cropW
    const imgH = cH * 100 / this.cropH
    // Offset so the crop region's top-left aligns with the container's top-left
    const imgX = -(this.cropX / 100) * imgW
    const imgY = -(this.cropY / 100) * imgH

    p.style.width = `${imgW}px`
    p.style.height = `${imgH}px`
    p.style.left = `${imgX}px`
    p.style.top = `${imgY}px`
    p.style.maxWidth = "none"
    p.style.margin = "0"
  }

  _updateCoords() {
    if (this.hasCoordsTarget) {
      this.coordsTarget.textContent = `${this.cropW.toFixed(0)}% x ${this.cropH.toFixed(0)}% (2:1 locked)`
    }
  }

  async save() {
    if (!this.updateUrl) return

    this.saveBtnTarget.disabled = true
    this.saveBtnTarget.textContent = "Saving..."

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.updateUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          crop_x: Math.round(this.cropX * 100) / 100,
          crop_y: Math.round(this.cropY * 100) / 100,
          crop_w: Math.round(this.cropW * 100) / 100,
          crop_h: Math.round(this.cropH * 100) / 100
        })
      })

      if (response.ok) {
        const btn = this.element.querySelector(`[data-photo-id="${this.photoId}"][data-action*="crop#open"]`)
        if (btn) {
          btn.dataset.cropX = this.cropX
          btn.dataset.cropY = this.cropY
          btn.dataset.cropW = this.cropW
          btn.dataset.cropH = this.cropH
        }
        this.close()
        if (window.showToast) window.showToast("success", "Crop saved! Thumbnail will update on next page load.")
      } else {
        alert("Failed to save crop.")
      }
    } catch (e) {
      alert("An error occurred. Please try again.")
    } finally {
      this.saveBtnTarget.disabled = false
      this.saveBtnTarget.textContent = "Save Crop"
    }
  }
}
